import styles from '../../styles/MateriasStyles/PantallaDeTareas.v2.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import TeachersTable from './TeachersTable';
import Toast from './Toast';
import { useState, useMemo, useEffect } from 'react';
import NuevoDocenteModal from './NuevoDocenteModal';
import { createTeacher, getTeachers, filterEmailsWithoutUser } from '../../api/teachers';
import { createUsuario } from '../../api/usuarios';
import LoaderOverlay from '../../components/LoaderOverlay';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../api/AuthContext';

function PantallaDeDocentes() {
  const { user: authUser } = useAuth();
  const { canCreate, canEdit, canView } = usePermissions();

  const puedeCrear =
    canCreate('Perfil Académico') ||
    canEdit('Perfil Académico') ||
    canView('Perfil Académico');

  // Mostrar botón solo para superadministrador
  const esSuperadmin = (authUser?.rol_nombre || '').toLowerCase() === 'superadministrador';

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [showMassCredentialsModal, setShowMassCredentialsModal] = useState(false);
  const [massCredentials, setMassCredentials] = useState([]);
  const [creatingProfiles, setCreatingProfiles] = useState(false);
  const [creationProgress, setCreationProgress] = useState(0);
  const [creationResults, setCreationResults] = useState([]);
  const [creationFinished, setCreationFinished] = useState(false);

  function generarPasswordDesdeEmail(email) {
    if (!email || typeof email !== 'string') {
      return `Lux2026!`;
    }
    const partes = email.split('@');
    const usuario = (partes[0] || '').trim();
    if (!usuario) {
      return `Lux2026!`;
    }
    return `${usuario}2026!`;
  }

  const handleGenerateMassCredentials = async () => {
    if (!teachers || teachers.length === 0) return;

    // Tomar solo emails válidos
    const emails = teachers
      .map((t) => t.email)
      .filter((e) => typeof e === 'string' && e.trim() !== '');

    let emailsSinUsuario = [];
    try {
      emailsSinUsuario = await filterEmailsWithoutUser(emails);
    } catch (e) {
      console.error('Error al filtrar asesores sin usuario:', e);
      emailsSinUsuario = emails; // fallback: asumir que ninguno existe
    }

    const emailsSet = new Set(emailsSinUsuario);

    const creds = teachers
      .filter((t) => t.email && emailsSet.has(t.email))
      .map((t) => ({
        nombre: t.nombre_completo || t.name || 'Sin nombre',
        email: t.email || 'No disponible',
        password: generarPasswordDesdeEmail(t.email),
        // Datos adicionales necesarios para crear el usuario en sys_usuarios
        id_institucion: t.id_institucion || null,
        id_campus: t.id_campus || null,
      }));

    setMassCredentials(creds);
    setCreationProgress(0);
    setCreationResults([]);
    setCreationFinished(false);
    setShowMassCredentialsModal(true);
  };

  // Cargar asesores
  const loadTeachers = async () => {
    try {
      setLoadingTeachers(true);
      const data = await getTeachers();
      setTeachers(data);
    } catch (e) {
      console.error('Error cargando docentes', e);
    } finally {
      setLoadingTeachers(false);
    }
  };

  useEffect(() => {
    loadTeachers();
    const onCreated = () => loadTeachers();
    const onChanged = () => loadTeachers();
    window.addEventListener('docente:created', onCreated);
    window.addEventListener('docente:changed', onChanged);
    return () => { 
      window.removeEventListener('docente:created', onCreated);
      window.removeEventListener('docente:changed', onChanged);
    };
  }, []);

  // Filtrado en tiempo real de asesores
  const filteredTeachers = useMemo(() => {
    if (!searchTerm.trim()) return teachers;
    
    const term = searchTerm.toLowerCase().trim();
    return teachers.filter((teacher) => {
      const name = (teacher.name || teacher.nombre_completo || '').toLowerCase();
      const empCode = (teacher.empCode || '').toLowerCase();
      const email = (teacher.email || '').toLowerCase();
      const academy = (teacher.academy || teacher.academia_nombre || '').toLowerCase();
      const nationality = (teacher.nationality || '').toLowerCase();
      const campus = (teacher.campus || '').toLowerCase();
      const country = (teacher.country || '').toLowerCase();
      
      return (
        name.includes(term) ||
        empCode.includes(term) ||
        email.includes(term) ||
        academy.includes(term) ||
        nationality.includes(term) ||
        campus.includes(term) ||
        country.includes(term)
      );
    });
  }, [teachers, searchTerm]);

  const handleSearch = () => {
    // El filtrado ya se aplica en tiempo real con searchTerm
    console.log('Buscando asesores con término:', searchTerm);
  };

  const handleCreateProfiles = async () => {
    if (!massCredentials || massCredentials.length === 0) return;

    setCreatingProfiles(true);
    setCreationProgress(0);
    setCreationResults([]);
    setCreationFinished(false);

    const total = massCredentials.length;
    let successCount = 0;
    let errorCount = 0;
    const results = [];

    for (let index = 0; index < total; index++) {
      const cred = massCredentials[index];

      const email = cred.email || '';
      const partes = email.split('@');
      const usuario = (partes[0] || '').trim() || email;

      const userData = {
        nombre_completo: cred.nombre || usuario,
        usuario,
        email,
        id_rol: 2, // Rol fijo para asesores
        id_institucion: cred.id_institucion || null,
        activo: true,
        campus_ids: cred.id_campus ? [cred.id_campus] : [],
        seccion_ids: [],
        password: cred.password,
      };

      try {
        await createUsuario(userData);
        successCount += 1;
        const entry = {
          email,
          nombre: cred.nombre || usuario,
          success: true,
          message: 'Usuario creado correctamente',
        };
        results.push(entry);
        setCreationResults([...results]);
      } catch (error) {
        console.error('Error al crear usuario asesor:', error);
        errorCount += 1;
        const entry = {
          email,
          nombre: cred.nombre || usuario,
          success: false,
          message: error?.message || 'Error al crear usuario',
        };
        results.push(entry);
        setCreationResults([...results]);
      }

      const progress = Math.round(((index + 1) / total) * 100);
      setCreationProgress(progress);
    }

    setCreationFinished(true);

    if (errorCount === 0) {
      window.dispatchEvent(new CustomEvent('toast:show', {
        detail: { type: 'success', message: `Perfiles creados correctamente (${successCount})` }
      }));
    } else {
      window.dispatchEvent(new CustomEvent('toast:show', {
        detail: { type: 'error', message: `Proceso completado con errores. Exitosos: ${successCount}, Errores: ${errorCount}` }
      }));
    }

    // Recargar asesores para que ya no aparezcan como "sin usuario"
    await loadTeachers();

    // Generar CSV automáticamente con el resultado del proceso
    if (results.length > 0) {
      const header = 'nombre,email,estado,mensaje\n';
      const rows = results.map((r) => {
        const safeNombre = (r.nombre || '').replace(/"/g, '""');
        const safeEmail = (r.email || '').replace(/"/g, '""');
        const estado = r.success ? 'CREADO' : 'ERROR';
        const safeMensaje = (r.message || '').replace(/"/g, '""');
        return `"${safeNombre}","${safeEmail}","${estado}","${safeMensaje}"`;
      }).join('\n');
      const csv = header + rows;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'usuarios_asesores_resultado_creacion.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    setCreatingProfiles(false);
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.layout}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <HeaderGlobal title="Asesores" />
          <main className={styles.mainContentDocentes}>
            <section className={styles.contentAreaDocentes}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loadingTeachers} label="Cargando asesores..." />
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.asesoresSearchRow}>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar Asesor..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSearch();
                          }}
                        />
                      </div>

                      <button
                        className={styles.buscarButtonAsesores}
                        onClick={handleSearch}
                      >
                        Buscar
                      </button>

                      {puedeCrear && (
                        <button
                          className={styles.newProgramButton}
                          onClick={() => setOpen(true)}
                        >
                          + Nuevo Asesor
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {esSuperadmin && (
                  <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
                    <button
                      style={{
                        background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '12px 24px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        letterSpacing: '0.3px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(0, 122, 255, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.3)';
                      }}
                      onClick={handleGenerateMassCredentials}
                      disabled={loadingTeachers || teachers.length === 0}
                    >
                      🔐 Generar Credenciales para Todos los Asesores
                    </button>
                  </div>
                )}
                <TeachersTable teachers={filteredTeachers} loading={loadingTeachers} />
                <NuevoDocenteModal
                  open={open}
                  onClose={() => setOpen(false)}
                  onSubmit={async (form) => {
                    setLoading(true);
                    try {
                      await createTeacher(form);
                      window.dispatchEvent(new CustomEvent('docente:created'));
                      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Asesor creado correctamente' } }));
                      setOpen(false);
                    } catch (e) {
                      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo crear el asesor' } }));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  title="Nuevo Asesor"
                />
              </div>
            </section>
          </main>
        </div>
      </div>
      <Toast />
      {showMassCredentialsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            padding: '32px',
            borderRadius: '20px',
            maxHeight: '85vh',
            maxWidth: '900px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <h3 style={{
              marginBottom: '24px',
              fontSize: '24px',
              fontWeight: '700',
              color: '#1d1d1f',
              letterSpacing: '-0.5px'
            }}>🔐 Credenciales Generadas para Asesores</h3>
            <div style={{
              backgroundColor: '#f5f5f7',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              flex: 1,
              overflowY: 'auto',
              minHeight: 0
            }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                <thead>
                  <tr>
                    <th style={{
                      borderBottom: '2px solid #d2d2d7',
                      padding: '12px',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6e6e73',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Nombre</th>
                    <th style={{
                      borderBottom: '2px solid #d2d2d7',
                      padding: '12px',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6e6e73',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Email</th>
                    <th style={{
                      borderBottom: '2px solid #d2d2d7',
                      padding: '12px',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6e6e73',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Contraseña</th>
                  </tr>
                </thead>
                <tbody>
                  {massCredentials.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        style={{
                          padding: '24px 12px',
                          textAlign: 'center',
                          fontSize: '14px',
                          color: '#6e6e73',
                          fontStyle: 'italic'
                        }}
                      >
                        No se encontraron usuarios sin perfil para generar credenciales.
                      </td>
                    </tr>
                  ) : (
                    massCredentials.map((cred, idx) => (
                      <tr key={idx} style={{ transition: 'background 0.2s ease' }}>
                        <td style={{
                          borderBottom: idx < massCredentials.length - 1 ? '1px solid #e5e5e7' : 'none',
                          padding: '12px',
                          fontSize: '14px',
                          color: '#1d1d1f'
                        }}>{cred.nombre}</td>
                        <td style={{
                          borderBottom: idx < massCredentials.length - 1 ? '1px solid #e5e5e7' : 'none',
                          padding: '12px',
                          fontSize: '14px',
                          color: '#1d1d1f'
                        }}>{cred.email}</td>
                        <td style={{
                          borderBottom: idx < massCredentials.length - 1 ? '1px solid #e5e5e7' : 'none',
                          padding: '12px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <code style={{
                              fontFamily: 'SF Mono, Monaco, Menlo, monospace',
                              fontSize: '13px',
                              backgroundColor: 'white',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              border: '1px solid #d2d2d7',
                              color: '#007AFF',
                              fontWeight: '500'
                            }}>{cred.password}</code>
                            <button
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '6px',
                                borderRadius: '6px',
                                transition: 'background 0.2s ease'
                              }}
                              onMouseEnter={(e) => { e.target.style.background = '#e5e5e7'; }}
                              onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
                              onClick={(e) => {
                                navigator.clipboard.writeText(cred.password);
                                const btn = e.target;
                                btn.textContent = '✓';
                                setTimeout(() => { btn.textContent = '📋'; }, 1000);
                              }}
                            >📋</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {(creatingProfiles || creationFinished || creationResults.length > 0) && (
              <div style={{
                marginBottom: '16px',
                padding: '12px 16px',
                borderRadius: '10px',
                backgroundColor: '#f5f5f7',
                border: '1px solid #e5e5e7',
                fontSize: '13px',
                color: '#1d1d1f'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>Progreso de creación de usuarios</span>
                  <span style={{ fontFamily: 'SF Mono, Monaco, Menlo, monospace' }}>{creationProgress}%</span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '999px',
                  backgroundColor: '#e5e5e7',
                  overflow: 'hidden',
                  marginBottom: creationResults.length > 0 ? '12px' : 0
                }}>
                  <div style={{
                    width: `${creationProgress}%`,
                    height: '100%',
                    borderRadius: '999px',
                    background: 'linear-gradient(90deg, #007AFF 0%, #34C759 100%)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                {creationResults.length > 0 && (
                  <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                    {creationFinished && (
                      <div style={{
                        marginBottom: '8px',
                        fontWeight: 600,
                        color: '#1d1d1f'
                      }}>
                        Resultado del proceso ({creationResults.filter(r => r.success).length} exitosos, {creationResults.filter(r => !r.success).length} con error)
                      </div>
                    )}
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {creationResults.map((r, idx) => (
                        <li
                          key={`${r.email}-${idx}`}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 0',
                            borderTop: idx === 0 ? 'none' : '1px solid #e5e5e7',
                            gap: '8px'
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontWeight: 500 }}>{r.nombre}</span>
                            {' '}
                            <span style={{ color: '#6e6e73' }}>&lt;{r.email}&gt;</span>
                          </span>
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            backgroundColor: r.success ? 'rgba(52,199,89,0.1)' : 'rgba(255,59,48,0.1)',
                            color: r.success ? '#34C759' : '#FF3B30',
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                          }}>
                            {r.success ? 'CREADO' : 'ERROR'}
                          </span>
                          {!r.success && (
                            <span style={{
                              fontSize: '11px',
                              color: '#FF3B30',
                              maxWidth: '40%',
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }} title={r.message}>
                              {r.message}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <button
              style={{
                backgroundColor: '#f5f5f7',
                color: '#007AFF',
                border: '1px solid #d2d2d7',
                borderRadius: '10px',
                padding: '10px 24px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: creatingProfiles || massCredentials.length === 0 ? 'default' : 'pointer',
                marginBottom: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                justifyContent: 'center',
                opacity: creatingProfiles || massCredentials.length === 0 ? 0.6 : 1
              }}
              onClick={creatingProfiles || massCredentials.length === 0 ? undefined : handleCreateProfiles}
              disabled={creatingProfiles || massCredentials.length === 0}
            >
              {creatingProfiles ? 'Creando perfiles...' : '👥 Crear perfiles'}
            </button>
            <button
              style={{
                background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 32px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                letterSpacing: '0.3px',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 122, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.3)';
              }}
              onClick={() => setShowMassCredentialsModal(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PantallaDeDocentes;
